/**
 * Statistics Page Component
 * Comprehensive analytics hub for trading performance metrics
 */

import {
  Flex,
  Box,
  useColorModeValue,
  Text,
  SimpleGrid,
  Skeleton,
  Alert,
  AlertIcon,
  AlertDescription,
  HStack,
  Switch,
  FormLabel,
  FormControl,
  Collapse,
} from "@chakra-ui/react";
import { toast } from "sonner";
import { presentMutationError, toastWarning } from "../../../utils/showApiErrorToast";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import CardHeader from "../../../components/Card/CardHeader";
import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
// Services
import { useUser } from "@clerk/clerk-react";
import { 
  useTradeStatistics, 
  useSymbolStatistics,
} from "../../../hooks/useTradeStatistics";
import { 
  fetchPortfolioGrowth,
  fetchWinRateTrend,
  fetchPnLOverTime,
  fetchDistribution,
  fetchRiskMetrics,
  fetchMonthlyPerformance,
  fetchWeeklyBreakdown,
  fetchDailyActivity,
  fetchComparativeAnalysis,
  fetchTimeAnalysis,
  fetchSymbolStatistics,
  fetchSessionPerformance,
} from "../../../services/tradeStatisticsApi";
// Types
import {
  TradeStatisticsFilters,
  DateRange,
} from "../../../types/trade";
import {
  buildTradePeriodLocalRangeQuery,
  endOfIanaCalendarDayUtc,
  formatDateAsCalendarDayInTimeZone,
  normalizeReportingZoneDateRangeToUtcBounds,
  resolveTradeListWindowQuery,
  startOfIanaCalendarDayUtc,
  subtractCalendarDaysFromYmd,
} from "../../../utils/notificationPeriod";
import { formatTradeDateOnlyForDisplay } from "../../../utils/displayDateTime";
import { StatisticsPageErrorBanner } from "../../../components/errors/StatisticsPageErrorBanner";
import { StatisticsPageErrorProvider } from "../../../hooks/StatisticsPageErrorContext";
import { useStatisticsPageErrors } from "../../../hooks/useStatisticsFetchErrors";
import { useActiveBrokerCapabilities } from "../../../hooks/useActiveBrokerCapabilities";
import { useActiveBrokerAccount } from "../../../hooks/useActiveBrokerAccount";
import { useDisplayCurrency } from "../../../contexts/DisplayCurrencyContext";
// Utils
import { exportStatisticsToExcel } from "../../../utils/exportStatistics";
import {
  portfolioGrowthChartTitle,
  portfolioGrowthBasisQueryParam,
} from "../../../utils/portfolioGrowthUi";
import { resolveDailyActivityTimezone } from "../../../utils/tradeAccountAnalytics";
// Components
import OverviewMetrics from "./components/OverviewMetrics";
import StatisticsPortfolioGrowthChart from "./components/StatisticsPortfolioGrowthChart";
import StatisticsWinRateChart from "./components/StatisticsWinRateChart";
import PnLOverTimeChart from "./components/PnLOverTimeChart";
import TradeDistributionCharts from "./components/TradeDistributionCharts";
import RiskMetricsCard from "./components/RiskMetricsCard";
import PerformanceBreakdown from "./components/PerformanceBreakdown";
import SystemQualityTable from "./components/SystemQualityTable";
import AveragesReturnsTable from "./components/AveragesReturnsTable";
import TimingCostsTable from "./components/TimingCostsTable";
import StreakAnalysis from "./components/StreakAnalysis";
import MonthlyPerformanceTable from "./components/MonthlyPerformanceTable";
import DailyActivityChart from "./components/DailyActivityChart";
import ComparativeAnalysis from "./components/ComparativeAnalysis";
// Detailed statistics now come from backend API only
import { DashboardHeaderExportButton } from "../Dashboard/components/DashboardHeaderExportButton";
import { StatisticsHeaderDateRangePicker } from "../Dashboard/components/StatisticsHeaderDateRangePicker";
import { NeedHelpButton } from "@/components/need-help-button";
import { TradeFilterSelectFullWidth } from "@/components/trade-filter-selects";
import { useIsBelowLg } from "@/hooks/use-mobile";
import { useDashboardDateWindowListener } from "../../../hooks/useDashboardDateWindowListener";
import { useUserNotificationTimezone } from "../../../hooks/useUserNotificationTimezone";
import {
  dateRangeForTradeLogFromPersisted,
  deriveTimeframeKeyFromLocalDateRange,
  equalDateRange,
  getInitialDashboardDateWindow,
  persistStateFromTradeLog,
  preferredPresetFromPersisted,
  writeDashboardDateWindow,
  type DashboardDatePresetTimeframe,
  type DashboardDateRangeChangeOptions,
} from "../../../utils/dashboardDateWindowStorage";
import {
  dateRangeFromLocalYmdWindow,
  parseStatisticsDeepLinkSearch,
  stripStatisticsDeepLinkWindowParams,
} from "../../../utils/statisticsDeepLink";
import { PageRouteHeadline } from "@/components/Layout/PageRouteHeadline";
import { LazyMountWhenVisible } from "@/components/LazyMountWhenVisible";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { useShadcnInsetHeaderActions } from "@/contexts/ShadcnInsetHeaderActionsContext";
import RiskMetricsTab from "./components/RiskMetricsTab";
import TimeAnalysisTab from "./components/TimeAnalysisTab";
import SymbolPerformanceTab from "./components/SymbolPerformanceTab";
import SessionPerformanceTab from "./components/SessionPerformanceTab";
import {
  StatisticsEmptyState,
  statisticsPageMinHeight,
  tradeStatisticsHasData,
} from "./components/statisticsEmptyState";

type StatisticsTab =
  | "overview"
  | "detailed"
  | "risk-metrics"
  | "time-analysis"
  | "symbol-performance"
  | "session-performance";

const STATISTICS_TAB_OPTIONS: Array<{ value: StatisticsTab; label: string }> = [
  { value: "overview", label: "Overview" },
  { value: "detailed", label: "Detailed" },
  { value: "risk-metrics", label: "Risk Metrics" },
  { value: "time-analysis", label: "Time Analysis" },
  { value: "symbol-performance", label: "Symbol Analysis" },
  { value: "session-performance", label: "Session Analysis" },
];

function Statistics() {
  const shadcnInset = useShadcnInsetChrome();
  const isBelowLg = useIsBelowLg();
  const { setActions: setInsetHeaderActions } = useShadcnInsetHeaderActions();
  const location = useLocation();
  const history = useHistory();
  const { user } = useUser();
  const { timezone: notificationTimezone, revision: notificationTzRevision } =
    useUserNotificationTimezone();
  const reportingTimeZone =
    notificationTimezone ??
    Intl.DateTimeFormat().resolvedOptions().timeZone ??
    "UTC";
  const { displayCurrency } = useDisplayCurrency();
  const {
    activeAccountCode,
    activeBrokerName,
    loading: tradeAccountsLoading,
  } = useActiveBrokerAccount();
  const textColor = useColorModeValue("#575757", "white");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const emptyPanelBg = useColorModeValue("gray.50", "rgba(0,0,0,0.25)");
  const secondaryTextColor = useColorModeValue("gray.400", "white");

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    // Notification / email deep links seed the calendar so URL window and UI stay aligned.
    const link = parseStatisticsDeepLinkSearch(
      typeof window !== "undefined" ? window.location.search : ""
    );
    if (link?.localStart && link?.localEnd) {
      const fromLink = dateRangeFromLocalYmdWindow(link.localStart, link.localEnd);
      if (fromLink) return fromLink;
    }
    return dateRangeForTradeLogFromPersisted(getInitialDashboardDateWindow());
  });
  const [selectedPresetId, setSelectedPresetId] =
    useState<DashboardDatePresetTimeframe | null>(() => {
      const link = parseStatisticsDeepLinkSearch(
        typeof window !== "undefined" ? window.location.search : ""
      );
      if (link?.localStart && link?.localEnd) return null;
      return preferredPresetFromPersisted(getInitialDashboardDateWindow());
    });
  const deepLinkWindowConsumedRef = useRef(false);
  const [comparisonEnabled, setComparisonEnabled] = useState(false);
  const [comparisonRange, setComparisonRange] = useState<DateRange | null>(null);
  const hasComparison = Boolean(
    comparisonEnabled && comparisonRange?.startDate && comparisonRange?.endDate
  );

  const timeframe = useMemo(
    () => deriveTimeframeKeyFromLocalDateRange(dateRange),
    [dateRange]
  );
  
  // Export loading state
  const [isExporting, setIsExporting] = useState(false);
  const [statisticsTab, setStatisticsTab] = useState<StatisticsTab>("overview");
  /** Once a tab is opened, keep it mounted so switching back does not refetch. */
  const [visitedStatisticsTabs, setVisitedStatisticsTabs] = useState<Set<StatisticsTab>>(
    () => new Set<StatisticsTab>(["overview"])
  );

  useEffect(() => {
    setVisitedStatisticsTabs((prev) => {
      if (prev.has(statisticsTab)) return prev;
      const next = new Set(prev);
      next.add(statisticsTab);
      return next;
    });
  }, [statisticsTab]);

  const statisticsTabPanelClassName =
    "mt-0 flex min-h-0 flex-1 flex-col px-0 data-[state=inactive]:hidden";

  // Use within-period statistics by default (compare end vs beginning of selected period)
  const comparisonType = "within-period";

  const brokerAccountCode = activeAccountCode;
  const brokerAccountCodeLoaded = !tradeAccountsLoading;

  // One-shot: apply notification window to calendar state, then drop date params from the URL
  // so later calendar changes aren't locked to the original deep-link query.
  useEffect(() => {
    if (deepLinkWindowConsumedRef.current) return;
    const link = parseStatisticsDeepLinkSearch(location.search);
    if (!link?.localStart || !link?.localEnd) {
      deepLinkWindowConsumedRef.current = true;
      return;
    }
    deepLinkWindowConsumedRef.current = true;

    const fromLink = dateRangeFromLocalYmdWindow(link.localStart, link.localEnd);
    if (fromLink) {
      setDateRange((prev) => (equalDateRange(prev, fromLink) ? prev : fromLink));
      setSelectedPresetId(null);
    }

    const nextSearch = stripStatisticsDeepLinkWindowParams(location.search);
    if (nextSearch !== (location.search || "")) {
      history.replace({ pathname: location.pathname, search: nextSearch });
    }
  }, [history, location.pathname, location.search]);

  // Don't render statistics UI (especially mini stat cards) until we know which account we're targeting.
  // Otherwise the page can briefly show fallback stats and then "switch" to the selected demo account.
  // IMPORTANT: never call analytics endpoints without an explicit account code; that can fan out to huge cohorts.
  const canFetchStats = Boolean(user?.id && brokerAccountCodeLoaded && brokerAccountCode && brokerAccountCode.trim());
  const {
    supportsPortfolioGrowth,
    supportsPortfolioBalanceFromBroker,
    accountAnalytics,
    defaultGrowthBasis,
  } = useActiveBrokerCapabilities(brokerAccountCode, activeBrokerName);

  const dailyActivityTimeZone = useMemo(
    () => resolveDailyActivityTimezone(accountAnalytics, reportingTimeZone),
    [accountAnalytics, reportingTimeZone]
  );

  const statisticsPortfolioGrowthTitle = useMemo(
    () =>
      portfolioGrowthChartTitle({
        supportsPortfolioGrowth,
        defaultGrowthBasis,
      }),
    [supportsPortfolioGrowth, defaultGrowthBasis]
  );
  
  useEffect(() => {
    writeDashboardDateWindow(
      persistStateFromTradeLog(dateRange, timeframe, selectedPresetId)
    );
  }, [dateRange, timeframe, selectedPresetId]);

  useDashboardDateWindowListener((p) => {
    const next = dateRangeForTradeLogFromPersisted(p);
    setDateRange((prev) => (equalDateRange(prev, next) ? prev : next));
    setSelectedPresetId(preferredPresetFromPersisted(p));
  });

  const handleDateRangeChange = useCallback(
    (newDateRange: DateRange, options?: DashboardDateRangeChangeOptions) => {
      setDateRange(newDateRange);
      if (options?.presetId) setSelectedPresetId(options.presetId);
      else setSelectedPresetId(null);
      if (!newDateRange.startDate && !newDateRange.endDate) {
        setComparisonEnabled(false);
        setComparisonRange(null);
      }
    },
    []
  );

  /**
   * GET /trades/statistics + /by-symbol accept local_* (server expands with stored TZ).
   * Chart/aux routes use ISO + timeframe; include explicit `timezone` so hooks’ cache keys
   * change when Display Preferences TZ changes (server may still apply stored TZ when omitted).
   */
  const listStatsFilters: TradeStatisticsFilters | undefined = useMemo(() => {
    if (!canFetchStats) return undefined;
    if (!brokerAccountCode || !brokerAccountCode.trim()) return undefined;
    const apiFilters: TradeStatisticsFilters = {};

    const window = resolveTradeListWindowQuery(
      dateRange,
      timeframe,
      reportingTimeZone,
      { includeTimezoneQuery: true }
    );
    if (window.kind === "local") {
      apiFilters.local_start_date = window.period.local_start_date;
      apiFilters.local_end_date = window.period.local_end_date;
      if (window.period.timezone) {
        apiFilters.timezone = window.period.timezone;
      }
    } else {
      apiFilters.timeframe = window.timeframe;
    }
    apiFilters.timezone = apiFilters.timezone ?? reportingTimeZone;

    apiFilters.broker_account_code = brokerAccountCode;
    if (activeBrokerName) {
      apiFilters.broker_name = activeBrokerName;
    }
    return apiFilters;
  }, [
    canFetchStats,
    dateRange,
    timeframe,
    brokerAccountCode,
    activeBrokerName,
    reportingTimeZone,
    notificationTzRevision,
  ]);

  // Cohort owner: listStatsFilters (local_* + timezone preferred, otherwise timeframe/ISO).
  // Use the same cohort-defining params for ALL analytics endpoints to prevent inconsistencies.
  const cohortFilters = listStatsFilters;

  const chartStatsFilters: TradeStatisticsFilters | undefined = useMemo(() => {
    if (!canFetchStats) return undefined;
    if (!brokerAccountCode || !brokerAccountCode.trim()) return undefined;
    const apiFilters: TradeStatisticsFilters = {};

    if (dateRange?.startDate || dateRange?.endDate) {
      if (dateRange.startDate && dateRange.endDate) {
        const { startDate, endDate } = normalizeReportingZoneDateRangeToUtcBounds(
          dateRange.startDate,
          dateRange.endDate,
          reportingTimeZone
        );
        apiFilters.start_date = startDate;
        apiFilters.end_date = endDate;
      } else {
        if (dateRange.startDate) {
          const ymd = formatDateAsCalendarDayInTimeZone(dateRange.startDate, reportingTimeZone);
          apiFilters.start_date = startOfIanaCalendarDayUtc(ymd, reportingTimeZone);
        }
        if (dateRange.endDate) {
          const ymd = formatDateAsCalendarDayInTimeZone(dateRange.endDate, reportingTimeZone);
          apiFilters.end_date = endOfIanaCalendarDayUtc(ymd, reportingTimeZone);
        }
      }
    } else {
      if (timeframe === "today") {
        apiFilters.timeframe = "today";
      } else if (timeframe) {
        apiFilters.timeframe = timeframe as "today" | "7-days" | "14-days" | "30-days" | "all-time";
      }
    }

    apiFilters.broker_account_code = brokerAccountCode;
    if (activeBrokerName) {
      apiFilters.broker_name = activeBrokerName;
    }
    apiFilters.timezone = apiFilters.timezone ?? reportingTimeZone;
    return apiFilters;
  }, [
    canFetchStats,
    dateRange,
    timeframe,
    brokerAccountCode,
    activeBrokerName,
    reportingTimeZone,
    notificationTzRevision,
  ]);

  const comparisonListStatsFilters = useMemo((): TradeStatisticsFilters | undefined => {
    if (!listStatsFilters) return undefined;
    if (!hasComparison || !comparisonRange?.startDate || !comparisonRange?.endDate) {
      return listStatsFilters;
    }
    const base: TradeStatisticsFilters = { ...listStatsFilters };
    delete base.local_start_date;
    delete base.local_end_date;
    delete base.start_date;
    delete base.end_date;
    delete base.timeframe;
    const period = buildTradePeriodLocalRangeQuery(
      comparisonRange.startDate,
      comparisonRange.endDate,
      { reportingTimeZone, includeTimezoneQuery: true }
    );
    return {
      ...base,
      local_start_date: period.local_start_date,
      local_end_date: period.local_end_date,
      timezone: period.timezone ?? reportingTimeZone,
    };
  }, [
    hasComparison,
    comparisonRange?.startDate,
    comparisonRange?.endDate,
    listStatsFilters,
    reportingTimeZone,
    notificationTzRevision,
  ]);

  const comparisonChartStatsFilters = useMemo((): TradeStatisticsFilters | undefined => {
    if (!chartStatsFilters) return undefined;
    if (!hasComparison || !comparisonRange?.startDate || !comparisonRange?.endDate) {
      return chartStatsFilters;
    }
    const base: TradeStatisticsFilters = { ...chartStatsFilters };
    delete base.start_date;
    delete base.end_date;
    delete base.timeframe;
    const { startDate, endDate } = normalizeReportingZoneDateRangeToUtcBounds(
      comparisonRange.startDate,
      comparisonRange.endDate,
      reportingTimeZone
    );
    return { ...base, start_date: startDate, end_date: endDate };
  }, [
    hasComparison,
    comparisonRange?.startDate,
    comparisonRange?.endDate,
    chartStatsFilters,
    reportingTimeZone,
  ]);

  // Distribution charts should represent realized outcomes (win/loss, long/short realized buckets),
  // so constrain them to closed trades to match the mini-cards' "closed trades" counts.
  const distributionClosedListFilters = useMemo<TradeStatisticsFilters | undefined>(() => {
    if (!listStatsFilters) return undefined;
    return {
      ...listStatsFilters,
      status: "closed",
    };
  }, [listStatsFilters]);

  const distributionClosedChartFilters = useMemo<TradeStatisticsFilters | undefined>(() => {
    if (!chartStatsFilters) return undefined;
    return {
      ...chartStatsFilters,
      status: "closed",
    };
  }, [chartStatsFilters]);

  const distributionClosedComparisonChartFilters = useMemo<TradeStatisticsFilters | undefined>(() => {
    if (!comparisonChartStatsFilters) return undefined;
    return {
      ...comparisonChartStatsFilters,
      status: "closed",
    };
  }, [comparisonChartStatsFilters]);

  // Fetch statistics from backend API only
  const { statistics: backendStats, loading: statsLoading, refetch: refetchStats } =
    useTradeStatistics(listStatsFilters);
  // Fetch comparison period stats when comparison is enabled
  const { statistics: comparisonBackendStats, loading: comparisonStatsLoading } =
    useTradeStatistics(comparisonListStatsFilters, { enabled: hasComparison });

  // Fetch symbol statistics from backend API
  const { symbolStats, refetch: refetchSymbolStats } = useSymbolStatistics(listStatsFilters);
  const { symbolStats: comparisonSymbolStats } = useSymbolStatistics(comparisonListStatsFilters, {
    enabled: hasComparison,
  });

  // Statistics from backend API only
  const statistics = useMemo(() => {
    if (!backendStats || statsLoading) {
      // Return empty stats while loading
      return {
        totalTrades: 0,
        openTrades: 0,
        closedTrades: 0,
        winningTrades: 0,
        losingTrades: 0,
        winRate: 0,
        totalPnL: 0,
        averageWin: 0,
        averageLoss: 0,
        largestWin: 0,
        largestLoss: 0,
        totalReturnPercent: 0,
        averageReturnPercent: 0,
      };
    }
    // Convert backend format to frontend format
    return {
      totalTrades: backendStats.total_trades,
      openTrades: backendStats.open_trades,
      closedTrades: backendStats.closed_trades,
      winningTrades: backendStats.winning_trades,
      losingTrades: backendStats.losing_trades,
      winRate: backendStats.win_rate,
      totalPnL: backendStats.total_pnl,
      averageWin: backendStats.average_win,
      averageLoss: backendStats.average_loss,
      largestWin: backendStats.largest_win,
      largestLoss: backendStats.largest_loss,
      totalReturnPercent: backendStats.total_return_percent,
      averageReturnPercent: backendStats.average_return_percent,
      mostTradedSymbol: backendStats.most_traded_symbol || undefined,
      mostTradedSymbolCount: backendStats.most_traded_symbol_count || undefined,
    };
  }, [backendStats, statsLoading]);


  // Comparison period statistics (for Performance Breakdown etc. when comparison is on)
  const comparisonStatistics = useMemo(() => {
    if (!comparisonBackendStats) {
      return {
        totalTrades: 0,
        openTrades: 0,
        closedTrades: 0,
        winningTrades: 0,
        losingTrades: 0,
        winRate: 0,
        totalPnL: 0,
        averageWin: 0,
        averageLoss: 0,
        largestWin: 0,
        largestLoss: 0,
        totalReturnPercent: 0,
        averageReturnPercent: 0,
      };
    }
    return {
      totalTrades: comparisonBackendStats.total_trades,
      openTrades: comparisonBackendStats.open_trades,
      closedTrades: comparisonBackendStats.closed_trades,
      winningTrades: comparisonBackendStats.winning_trades,
      losingTrades: comparisonBackendStats.losing_trades,
      winRate: comparisonBackendStats.win_rate,
      totalPnL: comparisonBackendStats.total_pnl,
      averageWin: comparisonBackendStats.average_win,
      averageLoss: comparisonBackendStats.average_loss,
      largestWin: comparisonBackendStats.largest_win,
      largestLoss: comparisonBackendStats.largest_loss,
      totalReturnPercent: comparisonBackendStats.total_return_percent,
      averageReturnPercent: comparisonBackendStats.average_return_percent,
      mostTradedSymbol: comparisonBackendStats.most_traded_symbol || undefined,
      mostTradedSymbolCount: comparisonBackendStats.most_traded_symbol_count || undefined,
    };
  }, [comparisonBackendStats]);

  // Listen for trade updates from broker sync (after all hooks are declared)
  useEffect(() => {
    if (!user?.id) return;

    const handleTradesUpdate = (event: Event) => {
      const action = (event as CustomEvent<{ action?: string }>)?.detail?.action;
      if (action === "marketDataPreference") return;
      // Refresh statistics when trades are updated
      refetchStats();
      if (refetchSymbolStats) refetchSymbolStats();
    };

    window.addEventListener("tradesUpdated", handleTradesUpdate);

    return () => {
      window.removeEventListener("tradesUpdated", handleTradesUpdate);
    };
  }, [user?.id, refetchStats, refetchSymbolStats]);

  // Portfolio size from backend API
  const portfolioSize = useMemo(() => {
    if (backendStats?.portfolio_value !== null && backendStats?.portfolio_value !== undefined) {
      return backendStats.portfolio_value;
    }
    // Fallback to total P&L if portfolio_value not available
    return statistics.totalPnL || 0;
  }, [backendStats?.portfolio_value, statistics.totalPnL]);

  const portfolioGrowthChangePercent = useMemo(() => {
    if (backendStats?.portfolio_change_percent !== null && backendStats?.portfolio_change_percent !== undefined) {
      return backendStats.portfolio_change_percent;
    }
    return 0;
  }, [backendStats?.portfolio_change_percent]);

  // Helper function to get date based on timeframe (matching Portfolio Growth)
  const getTimeframeDate = (timeframe: string): Date => {
    const now = new Date();
    const date = new Date(now);
    switch (timeframe) {
      case "7-days":
        date.setDate(date.getDate() - 7);
        break;
      case "14-days":
        date.setDate(date.getDate() - 14);
        break;
      case "30-days":
        date.setDate(date.getDate() - 30);
        break;
      case "90-days":
        date.setDate(date.getDate() - 90);
        break;
      case "all-time":
      default:
        return new Date(0);
    }
    return date;
  };

  // Win rate change percent from backend API
  const winRateChangePercent = useMemo(() => {
    if (backendStats?.win_rate_change_percent !== null && backendStats?.win_rate_change_percent !== undefined) {
      return backendStats.win_rate_change_percent;
    }
    return 0;
  }, [backendStats?.win_rate_change_percent]);

  // Comparison period stats (for duplicated cards when comparison is on)
  const comparisonPortfolioSize = useMemo(() => {
    if (!hasComparison || comparisonBackendStats?.portfolio_value == null) return 0;
    return comparisonBackendStats.portfolio_value;
  }, [hasComparison, comparisonBackendStats?.portfolio_value]);
  const comparisonPortfolioGrowthPercent = useMemo(() => {
    if (!hasComparison || comparisonBackendStats?.portfolio_change_percent == null) return 0;
    return comparisonBackendStats.portfolio_change_percent;
  }, [hasComparison, comparisonBackendStats?.portfolio_change_percent]);
  const comparisonWinRateChangePercent = useMemo(() => {
    if (!hasComparison || comparisonBackendStats?.win_rate_change_percent == null) return 0;
    return comparisonBackendStats.win_rate_change_percent;
  }, [hasComparison, comparisonBackendStats?.win_rate_change_percent]);
  const [comparisonLatestCumulativeWinRate, setComparisonLatestCumulativeWinRate] = useState(0);

  // Single day = same calendar day (any day), or explicit "today" timeframe with no custom dates
  const isSingleDaySelection = useMemo(() => {
    const sameReportingDay = (a: Date, b: Date) =>
      formatDateAsCalendarDayInTimeZone(a, reportingTimeZone) ===
      formatDateAsCalendarDayInTimeZone(b, reportingTimeZone);

    // Explicit timeframe "today" with no custom date range
    if (timeframe === "today" && !dateRange.startDate && !dateRange.endDate) return true;

    // Calendar: one day selected (start and end are the same day in reporting TZ)
    if (dateRange.startDate && dateRange.endDate) {
      return sameReportingDay(dateRange.startDate, dateRange.endDate);
    }

    return false;
  }, [timeframe, dateRange.startDate, dateRange.endDate, reportingTimeZone]);

  // Legacy alias for components that expect "today" wording
  const isTodaySelection = isSingleDaySelection;

  // Effective start/end for the current selection (used for monthly, weekly, and hourly logic)
  const effectiveDateRange = useMemo((): { startDate: Date; endDate: Date } | null => {
    if (dateRange.startDate && dateRange.endDate) {
      return normalizeReportingZoneDateRangeToUtcBounds(
        dateRange.startDate,
        dateRange.endDate,
        reportingTimeZone
      );
    }
    if (timeframe === "today") {
      const ymd = formatDateAsCalendarDayInTimeZone(new Date(), reportingTimeZone);
      return {
        startDate: startOfIanaCalendarDayUtc(ymd, reportingTimeZone),
        endDate: endOfIanaCalendarDayUtc(ymd, reportingTimeZone),
      };
    }
    if (timeframe === "7-days" || timeframe === "14-days" || timeframe === "30-days") {
      const days = timeframe === "7-days" ? 7 : timeframe === "14-days" ? 14 : 30;
      const endYmd = formatDateAsCalendarDayInTimeZone(new Date(), reportingTimeZone);
      const startYmd = subtractCalendarDaysFromYmd(endYmd, days, reportingTimeZone);
      return {
        startDate: startOfIanaCalendarDayUtc(startYmd, reportingTimeZone),
        endDate: endOfIanaCalendarDayUtc(endYmd, reportingTimeZone),
      };
    }
    return null;
  }, [timeframe, dateRange.startDate, dateRange.endDate, reportingTimeZone]);

  // Monthly Performance: keep cohort owner, but snap to full months in the same reporting timezone.
  const monthlyPerformanceFilters = useMemo((): TradeStatisticsFilters | undefined => {
    if (!cohortFilters) return undefined;
    const base: TradeStatisticsFilters = { ...cohortFilters } as any;
    delete (base as any).timeframe;
    delete (base as any).start_date;
    delete (base as any).end_date;
    if (!effectiveDateRange) return base;
    const startYmd = formatDateAsCalendarDayInTimeZone(
      effectiveDateRange.startDate,
      reportingTimeZone
    );
    const endYmd = formatDateAsCalendarDayInTimeZone(
      effectiveDateRange.endDate,
      reportingTimeZone
    );
    const sy = parseInt(startYmd.slice(0, 4), 10);
    const sm = parseInt(startYmd.slice(5, 7), 10);
    const ey = parseInt(endYmd.slice(0, 4), 10);
    const em = parseInt(endYmd.slice(5, 7), 10);
    const monthStartYmd = `${sy}-${String(sm).padStart(2, "0")}-01`;
    const lastDayEnd = new Date(ey, em, 0).getDate();
    const monthEndYmd = `${ey}-${String(em).padStart(2, "0")}-${String(lastDayEnd).padStart(2, "0")}`;
    return {
      ...base,
      local_start_date: monthStartYmd,
      local_end_date: monthEndYmd,
      timezone: base.timezone ?? reportingTimeZone,
    };
  }, [cohortFilters, effectiveDateRange, reportingTimeZone]);

  // Weekly Breakdown: exact calendar window (API buckets by week within the range)
  const weeklyBreakdownFilters = useMemo((): TradeStatisticsFilters | undefined => {
    if (!cohortFilters) return undefined;
    const base: TradeStatisticsFilters = { ...cohortFilters } as any;
    delete (base as any).timeframe;
    delete (base as any).start_date;
    delete (base as any).end_date;
    if (!effectiveDateRange) return base;
    const startYmd = formatDateAsCalendarDayInTimeZone(
      effectiveDateRange.startDate,
      reportingTimeZone
    );
    const endYmd = formatDateAsCalendarDayInTimeZone(
      effectiveDateRange.endDate,
      reportingTimeZone
    );
    return {
      ...base,
      local_start_date: startYmd,
      local_end_date: endYmd,
      timezone: base.timezone ?? reportingTimeZone,
    };
  }, [cohortFilters, effectiveDateRange, reportingTimeZone]);

  // Daily Activity: use hourly when only 1 day is selected
  const dailyActivityFilters = useMemo((): TradeStatisticsFilters | undefined => {
    if (!cohortFilters) return undefined;
    return {
      ...cohortFilters,
      period: isSingleDaySelection ? "hourly" : "daily",
      timezone: dailyActivityTimeZone,
    };
  }, [cohortFilters, isSingleDaySelection, dailyActivityTimeZone]);

  // Comparison period: effective date range and derived filters (weekly, daily)
  const comparisonEffectiveDateRange = useMemo((): { startDate: Date; endDate: Date } | null => {
    if (!hasComparison || !comparisonRange?.startDate || !comparisonRange?.endDate) return null;
    return normalizeReportingZoneDateRangeToUtcBounds(
      comparisonRange.startDate,
      comparisonRange.endDate,
      reportingTimeZone
    );
  }, [hasComparison, comparisonRange?.startDate, comparisonRange?.endDate, reportingTimeZone]);
  const comparisonWeeklyBreakdownFilters = useMemo((): TradeStatisticsFilters | undefined => {
    if (!comparisonListStatsFilters) return undefined;
    if (
      !comparisonListStatsFilters.local_start_date ||
      !comparisonListStatsFilters.local_end_date
    ) {
      return undefined;
    }
    const base: TradeStatisticsFilters = { ...comparisonListStatsFilters };
    delete (base as any).timeframe;
    delete (base as any).start_date;
    delete (base as any).end_date;
    return base;
  }, [comparisonListStatsFilters]);
  const comparisonDailyActivityFilters = useMemo((): TradeStatisticsFilters | undefined => {
    if (!comparisonChartStatsFilters) return undefined;
    return {
      ...comparisonChartStatsFilters,
      period: isSingleDaySelection ? "hourly" : "daily",
      timezone: dailyActivityTimeZone,
    };
  }, [comparisonChartStatsFilters, isSingleDaySelection, dailyActivityTimeZone]);

  // Monthly performance comparison: only show comparison card when selected and comparison are in different month(s)/year(s)
  const selectedMonthKeys = useMemo(() => {
    if (!effectiveDateRange) return new Set<string>();
    const startYmd = formatDateAsCalendarDayInTimeZone(
      effectiveDateRange.startDate,
      reportingTimeZone
    );
    const endYmd = formatDateAsCalendarDayInTimeZone(
      effectiveDateRange.endDate,
      reportingTimeZone
    );
    let y = parseInt(startYmd.slice(0, 4), 10);
    let m = parseInt(startYmd.slice(5, 7), 10);
    const ey = parseInt(endYmd.slice(0, 4), 10);
    const em = parseInt(endYmd.slice(5, 7), 10);
    const set = new Set<string>();
    while (y < ey || (y === ey && m <= em)) {
      set.add(`${y}-${m}`);
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
    }
    return set;
  }, [effectiveDateRange, reportingTimeZone]);
  const comparisonMonthKeys = useMemo(() => {
    if (!comparisonEffectiveDateRange) return new Set<string>();
    const startYmd = formatDateAsCalendarDayInTimeZone(
      comparisonEffectiveDateRange.startDate,
      reportingTimeZone
    );
    const endYmd = formatDateAsCalendarDayInTimeZone(
      comparisonEffectiveDateRange.endDate,
      reportingTimeZone
    );
    let y = parseInt(startYmd.slice(0, 4), 10);
    let m = parseInt(startYmd.slice(5, 7), 10);
    const ey = parseInt(endYmd.slice(0, 4), 10);
    const em = parseInt(endYmd.slice(5, 7), 10);
    const set = new Set<string>();
    while (y < ey || (y === ey && m <= em)) {
      set.add(`${y}-${m}`);
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
    }
    return set;
  }, [comparisonEffectiveDateRange, reportingTimeZone]);
  const showMonthlyPerformanceComparison = useMemo(() => {
    if (!hasComparison || selectedMonthKeys.size === 0 || comparisonMonthKeys.size === 0) return false;
    if (selectedMonthKeys.size !== comparisonMonthKeys.size) return true;
    const selectedArr = Array.from(selectedMonthKeys);
    if (selectedArr.some((k) => !comparisonMonthKeys.has(k))) return true;
    return false;
  }, [hasComparison, selectedMonthKeys, comparisonMonthKeys]);
  const comparisonMonthlyPerformanceFilters = useMemo((): TradeStatisticsFilters => {
    const base = { ...comparisonChartStatsFilters } as TradeStatisticsFilters;
    delete (base as any).timeframe;
    delete (base as any).local_start_date;
    delete (base as any).local_end_date;
    if (!comparisonEffectiveDateRange) return base;
    const startYmd = formatDateAsCalendarDayInTimeZone(
      comparisonEffectiveDateRange.startDate,
      reportingTimeZone
    );
    const endYmd = formatDateAsCalendarDayInTimeZone(
      comparisonEffectiveDateRange.endDate,
      reportingTimeZone
    );
    const sy = parseInt(startYmd.slice(0, 4), 10);
    const sm = parseInt(startYmd.slice(5, 7), 10);
    const ey = parseInt(endYmd.slice(0, 4), 10);
    const em = parseInt(endYmd.slice(5, 7), 10);
    const monthStartYmd = `${sy}-${String(sm).padStart(2, "0")}-01`;
    const lastDayEnd = new Date(ey, em, 0).getDate();
    const monthEndYmd = `${ey}-${String(em).padStart(2, "0")}-${String(lastDayEnd).padStart(2, "0")}`;
    return {
      ...base,
      start_date: startOfIanaCalendarDayUtc(monthStartYmd, reportingTimeZone),
      end_date: endOfIanaCalendarDayUtc(monthEndYmd, reportingTimeZone),
    };
  }, [comparisonChartStatsFilters, comparisonEffectiveDateRange, reportingTimeZone]);

  // Heavy chart endpoints are fetched by the visible tab/widget, not all at once.
  // Excel export still loads them on demand in handleExportXls.

  // Latest cumulative win rate from Win Rate Trend chart (for ChartCard display)
  const [latestCumulativeWinRate, setLatestCumulativeWinRate] = useState<number>(0);

  // Check if we have statistics data
  const hasData = backendStats !== null && !statsLoading;
  // Check if we're in initial loading state (no data yet - show skeletons immediately on page load)
  // This ensures skeletons show as soon as the page loads, even before the hook sets loading to true
  // Show skeletons when: no data AND (loading OR user is logged in but no data yet)
  const isInitialLoading = backendStats === null && (statsLoading || user?.id !== undefined);
  // Show loading message when loading (either initial load or when date range changes)
  const showLoadingMessage = isInitialLoading || statsLoading;
  // Check if a date range is selected
  const hasDateRangeSelected = dateRange.startDate !== null || dateRange.endDate !== null;

  // Export handler
  const handleExportXls = useCallback(async () => {
    if (!backendStats) {
      toastWarning("No data to export", {
        description: "Please wait for statistics to load before exporting.",
      });
      return;
    }

    setIsExporting(true);
    try {
      // Fetch all data explicitly to ensure we have the latest data
      // Use Promise.allSettled to fetch all data in parallel and handle errors gracefully
      const [
        symbolStatsResult,
        riskMetricsResult,
        timeAnalysisResult,
        pnlOverTimeResult,
        distributionResult,
        monthlyPerformanceResult,
        weeklyBreakdownResult,
        dailyActivityResult,
        comparativeAnalysisResult,
        portfolioGrowthResult,
        winRateTrendResult,
        sessionPerformanceResult,
      ] = await Promise.allSettled([
        fetchSymbolStatistics(listStatsFilters),
        fetchRiskMetrics(listStatsFilters),
        fetchTimeAnalysis(listStatsFilters),
        fetchPnLOverTime(listStatsFilters, isSingleDaySelection ? "hourly" : "daily"),
        fetchDistribution(listStatsFilters),
        fetchMonthlyPerformance(monthlyPerformanceFilters),
        fetchWeeklyBreakdown(weeklyBreakdownFilters),
        fetchDailyActivity(dailyActivityFilters),
        fetchComparativeAnalysis(listStatsFilters),
        brokerAccountCode && chartStatsFilters
          ? fetchPortfolioGrowth(brokerAccountCode, {
              timeframe: timeframe as "today" | "7-days" | "14-days" | "30-days" | "all-time",
              startDate: chartStatsFilters.start_date,
              endDate: chartStatsFilters.end_date,
              sampling: isSingleDaySelection ? "hourly" : "daily",
              timezone: chartStatsFilters.timezone,
              ...(activeBrokerName ? { broker_name: activeBrokerName } : {}),
              ...((() => {
                const gb = portfolioGrowthBasisQueryParam(supportsPortfolioGrowth);
                return gb ? { growth_basis: gb } : {};
              })()),
            })
          : Promise.resolve(null),
        brokerAccountCode && listStatsFilters
          ? fetchWinRateTrend(brokerAccountCode, {
              period: isSingleDaySelection ? "hourly" : "daily",
              ...(listStatsFilters.local_start_date && listStatsFilters.local_end_date
                ? {
                    local_start_date: listStatsFilters.local_start_date,
                    local_end_date: listStatsFilters.local_end_date,
                    timezone: listStatsFilters.timezone,
                  }
                : {
                    startDate: listStatsFilters.start_date,
                    endDate: listStatsFilters.end_date,
                    timeframe: listStatsFilters.timeframe as any,
                    timezone: listStatsFilters.timezone,
                  }),
              ...(activeBrokerName ? { broker_name: activeBrokerName } : {}),
            })
          : Promise.resolve(null),
        fetchSessionPerformance(listStatsFilters),
      ]);

      // Extract data from results, using null if rejected
      const symbolStatsData = symbolStatsResult.status === "fulfilled" ? symbolStatsResult.value : null;
      const riskMetricsData = riskMetricsResult.status === "fulfilled" ? riskMetricsResult.value : null;
      const timeAnalysisData = timeAnalysisResult.status === "fulfilled" ? timeAnalysisResult.value : null;
      const pnlOverTimeData = pnlOverTimeResult.status === "fulfilled" ? pnlOverTimeResult.value : null;
      const distributionData = distributionResult.status === "fulfilled" ? distributionResult.value : null;
      const monthlyPerformanceData = monthlyPerformanceResult.status === "fulfilled" ? monthlyPerformanceResult.value : null;
      const weeklyBreakdownData = weeklyBreakdownResult.status === "fulfilled" ? weeklyBreakdownResult.value : null;
      const dailyActivityData = dailyActivityResult.status === "fulfilled" ? dailyActivityResult.value : null;
      const comparativeAnalysisData = comparativeAnalysisResult.status === "fulfilled" ? comparativeAnalysisResult.value : null;
      const portfolioGrowthData = portfolioGrowthResult.status === "fulfilled" ? portfolioGrowthResult.value : null;
      const winRateTrendData = winRateTrendResult.status === "fulfilled" ? winRateTrendResult.value : null;
      const sessionPerformanceData = sessionPerformanceResult.status === "fulfilled" ? sessionPerformanceResult.value : null;

      // Log any failures for debugging
      const failures = [
        symbolStatsResult,
        riskMetricsResult,
        timeAnalysisResult,
        pnlOverTimeResult,
        distributionResult,
        monthlyPerformanceResult,
        weeklyBreakdownResult,
        dailyActivityResult,
        comparativeAnalysisResult,
        portfolioGrowthResult,
        winRateTrendResult,
        sessionPerformanceResult,
      ].filter((result) => result.status === "rejected");
      
      if (failures.length > 0) {
        console.warn("Some data failed to fetch for export:", failures.map((f) => (f as PromiseRejectedResult).reason));
      }

      // When comparison period is selected, fetch comparison data for export
      let comparisonExportData: {
        comparisonStatistics: typeof comparisonBackendStats;
        comparisonDateRange: { startDate: Date | null; endDate: Date | null };
        comparisonSymbolStats: Awaited<ReturnType<typeof fetchSymbolStatistics>> | null;
        comparisonRiskMetrics: Awaited<ReturnType<typeof fetchRiskMetrics>> | null;
        comparisonTimeAnalysis: Awaited<ReturnType<typeof fetchTimeAnalysis>> | null;
        comparisonSessionPerformance: Awaited<ReturnType<typeof fetchSessionPerformance>> | null;
        comparisonMonthlyPerformance: Awaited<ReturnType<typeof fetchMonthlyPerformance>> | null;
        comparisonWeeklyBreakdown: Awaited<ReturnType<typeof fetchWeeklyBreakdown>> | null;
        comparisonDailyActivity: Awaited<ReturnType<typeof fetchDailyActivity>> | null;
        comparisonComparativeAnalysis: Awaited<ReturnType<typeof fetchComparativeAnalysis>> | null;
      } | null = null;

      if (hasComparison && comparisonRange?.startDate && comparisonRange?.endDate) {
        const [
          compSymbolResult,
          compRiskResult,
          compTimeResult,
          compSessionResult,
          compMonthlyResult,
          compWeeklyResult,
          compDailyResult,
          compComparativeResult,
        ] = await Promise.allSettled([
          fetchSymbolStatistics(comparisonListStatsFilters),
          fetchRiskMetrics(comparisonChartStatsFilters),
          fetchTimeAnalysis(comparisonChartStatsFilters),
          fetchSessionPerformance(comparisonChartStatsFilters),
          fetchMonthlyPerformance(comparisonMonthlyPerformanceFilters),
          fetchWeeklyBreakdown(comparisonWeeklyBreakdownFilters),
          fetchDailyActivity(comparisonDailyActivityFilters),
          fetchComparativeAnalysis(comparisonChartStatsFilters),
        ]);
        comparisonExportData = {
          comparisonStatistics: comparisonBackendStats,
          comparisonDateRange: {
            startDate: comparisonRange.startDate,
            endDate: comparisonRange.endDate,
          },
          comparisonSymbolStats: compSymbolResult.status === "fulfilled" ? compSymbolResult.value : null,
          comparisonRiskMetrics: compRiskResult.status === "fulfilled" ? compRiskResult.value : null,
          comparisonTimeAnalysis: compTimeResult.status === "fulfilled" ? compTimeResult.value : null,
          comparisonSessionPerformance: compSessionResult.status === "fulfilled" ? compSessionResult.value : null,
          comparisonMonthlyPerformance: compMonthlyResult.status === "fulfilled" ? compMonthlyResult.value : null,
          comparisonWeeklyBreakdown: compWeeklyResult.status === "fulfilled" ? compWeeklyResult.value : null,
          comparisonDailyActivity: compDailyResult.status === "fulfilled" ? compDailyResult.value : null,
          comparisonComparativeAnalysis: compComparativeResult.status === "fulfilled" ? compComparativeResult.value : null,
        };
      }

      // Export all data (with comparison when selected)
      await exportStatisticsToExcel({
        displayCurrency,
        statistics: backendStats,
        symbolStats: symbolStatsData,
        riskMetrics: riskMetricsData,
        timeAnalysis: timeAnalysisData,
        portfolioGrowth: portfolioGrowthData,
        winRateTrend: winRateTrendData,
        pnlOverTime: pnlOverTimeData,
        distribution: distributionData,
        monthlyPerformance: monthlyPerformanceData,
        weeklyBreakdown: weeklyBreakdownData,
        dailyActivity: dailyActivityData,
        comparativeAnalysis: comparativeAnalysisData,
        sessionPerformance: sessionPerformanceData,
        filters: chartStatsFilters,
        timeframe,
        dateRange,
        ...(comparisonExportData && {
          comparisonStatistics: comparisonExportData.comparisonStatistics,
          comparisonDateRange: comparisonExportData.comparisonDateRange,
          comparisonSymbolStats: comparisonExportData.comparisonSymbolStats,
          comparisonRiskMetrics: comparisonExportData.comparisonRiskMetrics,
          comparisonTimeAnalysis: comparisonExportData.comparisonTimeAnalysis,
          comparisonSessionPerformance: comparisonExportData.comparisonSessionPerformance,
          comparisonMonthlyPerformance: comparisonExportData.comparisonMonthlyPerformance,
          comparisonWeeklyBreakdown: comparisonExportData.comparisonWeeklyBreakdown,
          comparisonDailyActivity: comparisonExportData.comparisonDailyActivity,
          comparisonComparativeAnalysis: comparisonExportData.comparisonComparativeAnalysis,
        }),
      });

      toast.success("Export successful", {
        description: "Statistics have been exported to Excel.",
      });
    } catch (error) {
      console.error("Error exporting statistics:", error);
      presentMutationError(error, "Export failed");
    } finally {
      setIsExporting(false);
    }
  }, [
    backendStats,
    comparisonBackendStats,
    comparisonRange,
    comparisonListStatsFilters,
    comparisonChartStatsFilters,
    comparisonMonthlyPerformanceFilters,
    comparisonWeeklyBreakdownFilters,
    comparisonDailyActivityFilters,
    hasComparison,
    listStatsFilters,
    chartStatsFilters,
    timeframe,
    dateRange,
    brokerAccountCode,
    isSingleDaySelection,
    monthlyPerformanceFilters,
    weeklyBreakdownFilters,
    dailyActivityFilters,
    supportsPortfolioGrowth,
    activeBrokerName,
  ]);

  const handleExportPdf = useCallback(() => {
    window.print();
  }, []);

  const headerToolbar = useMemo(
    () => (
      <HStack spacing={3} align="center" flexWrap="nowrap" justify="flex-end">
        <StatisticsHeaderDateRangePicker
          dateRange={dateRange}
          selectedPresetId={selectedPresetId}
          onDateRangeChange={handleDateRangeChange}
          comparisonEnabled={comparisonEnabled}
          onComparisonEnabledChange={setComparisonEnabled}
          comparisonRange={comparisonRange}
          onComparisonRangeChange={setComparisonRange}
        />
        <DashboardHeaderExportButton
          menuOptions={[
            {
              id: "xls",
              label: "Export XLS",
              onSelect: handleExportXls,
              disabled: !hasData || isExporting,
            },
            {
              id: "pdf",
              label: "Export PDF",
              onSelect: handleExportPdf,
              disabled: !hasData,
            },
          ]}
          isLoading={isExporting}
          disabled={!hasData}
          aria-label="Export statistics"
          iconOnly={isBelowLg}
        />
        <NeedHelpButton
          href="https://help.tesseratrades.com/statistics"
          iconOnly={isBelowLg}
        />
      </HStack>
    ),
    [
      isExporting,
      hasData,
      handleExportXls,
      handleExportPdf,
      dateRange,
      selectedPresetId,
      handleDateRangeChange,
      comparisonEnabled,
      comparisonRange,
      isBelowLg,
    ],
  );

  useEffect(() => {
    if (!shadcnInset) {
      setInsetHeaderActions(null);
      return;
    }
    setInsetHeaderActions(headerToolbar);
    return () => setInsetHeaderActions(null);
  }, [shadcnInset, headerToolbar, setInsetHeaderActions]);

  const titleRowMb =
    comparisonEnabled && comparisonRange?.startDate && comparisonRange?.endDate
      ? 0
      : "24px";

  const pageMinHeight = statisticsPageMinHeight(shadcnInset);

  const { pageError, pageErrorAction, suppressWidgetErrors } = useStatisticsPageErrors({
    headlineKey: "statistics",
  });

  const handleStatisticsPageRetry = useCallback(() => {
    refetchStats();
    refetchSymbolStats();
    window.dispatchEvent(new CustomEvent("tradesUpdated", { detail: { action: "refetch" } }));
  }, [refetchStats, refetchSymbolStats]);

  // Extract Flex props to avoid TypeScript union type complexity error
  const mainFlexProps = {
    direction: "column" as const,
    w: "100%",
    minH: pageMinHeight,
    pt: shadcnInset ? 0 : { base: "100px", md: "80px", lg: "40px" },
    pb: shadcnInset ? 0 : { base: "100px", md: "80px", lg: "40px" },
  };

  return (
    <StatisticsPageErrorProvider suppressWidgetErrors={suppressWidgetErrors}>
    {/* @ts-ignore - Complex responsive props cause union type inference issues */}
    <Flex data-statistics-page="" {...mainFlexProps}>
      {/* Page title + export/date — inset shell moves controls to breadcrumb bar */}
      {!shadcnInset ? (
        <Flex
          w="100%"
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          mb={titleRowMb}
          gap={4}
          direction={{ base: "column", md: "row" }}
        >
          <PageRouteHeadline>Statistics</PageRouteHeadline>
          <Box w={{ base: "100%", sm: "auto" }} flexShrink={0}>
            {headerToolbar}
          </Box>
        </Flex>
      ) : null}

      {pageError ? (
        <Box w="100%" mb={4}>
          <StatisticsPageErrorBanner
            message={pageError}
            action={pageErrorAction ?? undefined}
            onRetry={handleStatisticsPageRetry}
          />
        </Box>
      ) : null}

      {/* Gap row: when comparison on, show "Compare: date range" vertically centered in the 24px gap */}
      {comparisonEnabled &&
      comparisonRange?.startDate &&
      comparisonRange?.endDate ? (
        <Box
          h="24px"
          w="100%"
          display="flex"
          alignItems="center"
          justifyContent={{ base: "flex-start", sm: "flex-end" }}
          minW={0}
        >
          <Text
            fontSize="sm"
            fontFamily="'IBM Plex Sans', sans-serif"
            color={secondaryTextColor}
            noOfLines={1}
            lineHeight="24px"
            display="block"
          >
            Compare:{" "}
            {formatTradeDateOnlyForDisplay(
              comparisonRange.startDate,
              reportingTimeZone
            )}
            {" – "}
            {formatTradeDateOnlyForDisplay(
              comparisonRange.endDate,
              reportingTimeZone
            )}
          </Text>
        </Box>
      ) : null}

      {/* Loading state with animated ellipsis */}
      {showLoadingMessage ? (
        <Flex flex="1" minH="0" w="100%" align="center" justify="center">
          <StatisticsEmptyState variant="loading" fillParent />
        </Flex>
      ) : null}

      {/* Tabs — same shadcn pill tabs as Trade Log / Trade Review */}
      {hasData && (
        <Tabs
          data-trade-log-tabs=""
          className="flex min-h-0 w-full flex-1 flex-col"
          value={statisticsTab}
          onValueChange={(value) => setStatisticsTab(value as StatisticsTab)}
        >
          {isBelowLg ? (
            <div
              data-statistics-section-select=""
              className="mb-3 w-full min-w-0"
            >
              <TradeFilterSelectFullWidth
                id="statistics-section-select"
                value={statisticsTab}
                onValueChange={(value) => setStatisticsTab(value as StatisticsTab)}
                options={STATISTICS_TAB_OPTIONS}
              />
            </div>
          ) : (
            <div className="w-full overflow-x-auto overflow-y-hidden">
              <TabsList className="min-w-max">
                {STATISTICS_TAB_OPTIONS.map((option) => (
                  <TabsTrigger key={option.value} value={option.value}>
                    {option.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
          )}

          <TabsContent
            value="overview"
            forceMount
            className={statisticsTabPanelClassName}
          >
              {!tradeStatisticsHasData(backendStats) ? (
                <Flex flex="1" minH="0" w="100%" align="center" justify="center">
                  <StatisticsEmptyState variant="no-data" fillParent />
                </Flex>
              ) : (
              <>
              {/* Overview Metrics */}
              <Box mb="24px">
                <OverviewMetrics
                  statistics={statistics}
                  backendStats={backendStats}
                  comparisonType={comparisonType}
                  brokerAccountCode={brokerAccountCode ?? undefined}
                  activeBrokerName={activeBrokerName}
                  supportsPortfolioBalanceFromBroker={supportsPortfolioBalanceFromBroker}
                  supportsPortfolioGrowth={supportsPortfolioGrowth}
                  expectancyValue={
                    (backendStats as any)?.expectancy ??
                    (backendStats as any)?.average_per_trade_pnl ??
                    null
                  }
                />
              </Box>

              {/* Charts — below-fold sections mount once scrolled near viewport */}
              <>
                {/* Portfolio Growth and Win Rate — when comparison on, duplicate with blue (selected) / yellow (comparison) stroke */}
                <LazyMountWhenVisible minHeight={360} data-lazy-mount="overview-growth-winrate">
                {hasComparison ? (
                  <>
                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="24px" mb="24px">
                      <Box data-statistics-card-surface="" border="2px solid" borderColor="#38BBD1" borderRadius="lg" p={1} bg={cardBg}>
<StatisticsPortfolioGrowthChart
                          brokerAccountCode={brokerAccountCode || ""}
                          timeframe={timeframe as "today" | "7-days" | "14-days" | "30-days" | "all-time"}
                          startDate={chartStatsFilters?.start_date}
                          endDate={chartStatsFilters?.end_date}
                          reportingTimezone={reportingTimeZone}
                          portfolioSize={portfolioSize}
                          percentage={portfolioGrowthChangePercent ?? 0}
                          useHourly={isSingleDaySelection}
                          title={statisticsPortfolioGrowthTitle}
                          brokerName={activeBrokerName}
                          supportsPortfolioGrowth={supportsPortfolioGrowth}
                          defaultGrowthBasis={defaultGrowthBasis}
                        />
                      </Box>
                      <Box data-statistics-card-surface="" border="2px solid" borderColor="#38BBD1" borderRadius="lg" p={1} bg={cardBg}>
<StatisticsWinRateChart
                          brokerAccountCode={brokerAccountCode || ""}
                          timeframe={timeframe as "today" | "7-days" | "14-days" | "30-days" | "all-time"}
                          localStartDate={listStatsFilters?.local_start_date}
                          localEndDate={listStatsFilters?.local_end_date}
                          reportingTimezone={reportingTimeZone}
                          percentage={winRateChangePercent ?? 0}
                          value={latestCumulativeWinRate || 0}
                          valueFormatter={(val) => `${val.toFixed(2)}%`}
                          onLatestCumulativeWinRateChange={setLatestCumulativeWinRate}
                          useHourly={isSingleDaySelection}
                        />
                      </Box>
                    </SimpleGrid>
                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="24px" mb="24px">
                      <Box data-statistics-card-surface="" border="2px solid" borderColor="#F49113" borderRadius="lg" p={1} bg={cardBg}>
<StatisticsPortfolioGrowthChart
                          brokerAccountCode={brokerAccountCode || ""}
                          timeframe={timeframe as "today" | "7-days" | "14-days" | "30-days" | "all-time"}
                          startDate={comparisonChartStatsFilters?.start_date}
                          endDate={comparisonChartStatsFilters?.end_date}
                          reportingTimezone={reportingTimeZone}
                          portfolioSize={comparisonPortfolioSize}
                          percentage={comparisonPortfolioGrowthPercent}
                          useHourly={isSingleDaySelection}
                          title={statisticsPortfolioGrowthTitle}
                          brokerName={activeBrokerName}
                          supportsPortfolioGrowth={supportsPortfolioGrowth}
                          defaultGrowthBasis={defaultGrowthBasis}
                        />
                      </Box>
                      <Box data-statistics-card-surface="" border="2px solid" borderColor="#F49113" borderRadius="lg" p={1} bg={cardBg}>
<StatisticsWinRateChart
                          brokerAccountCode={brokerAccountCode || ""}
                          timeframe={timeframe as "today" | "7-days" | "14-days" | "30-days" | "all-time"}
                          localStartDate={comparisonListStatsFilters?.local_start_date}
                          localEndDate={comparisonListStatsFilters?.local_end_date}
                          reportingTimezone={reportingTimeZone}
                          percentage={comparisonWinRateChangePercent}
                          value={comparisonLatestCumulativeWinRate || 0}
                          valueFormatter={(val) => `${val.toFixed(2)}%`}
                          onLatestCumulativeWinRateChange={setComparisonLatestCumulativeWinRate}
                          useHourly={isSingleDaySelection}
                        />
                      </Box>
                    </SimpleGrid>
                  </>
                ) : (
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="24px" mb="24px">
                    <StatisticsPortfolioGrowthChart
                      brokerAccountCode={brokerAccountCode || ""}
                      timeframe={timeframe as "today" | "7-days" | "14-days" | "30-days" | "all-time"}
                      startDate={chartStatsFilters?.start_date}
                      endDate={chartStatsFilters?.end_date}
                      reportingTimezone={reportingTimeZone}
                      portfolioSize={portfolioSize}
                      percentage={portfolioGrowthChangePercent ?? 0}
                      useHourly={isSingleDaySelection}
                      title={statisticsPortfolioGrowthTitle}
                      brokerName={activeBrokerName}
                      supportsPortfolioGrowth={supportsPortfolioGrowth}
                      defaultGrowthBasis={defaultGrowthBasis}
                    />
                    <StatisticsWinRateChart
                      brokerAccountCode={brokerAccountCode || ""}
                      timeframe={timeframe as "today" | "7-days" | "14-days" | "30-days" | "all-time"}
                      localStartDate={listStatsFilters?.local_start_date}
                      localEndDate={listStatsFilters?.local_end_date}
                      reportingTimezone={reportingTimeZone}
                      percentage={winRateChangePercent ?? 0}
                      value={latestCumulativeWinRate || 0}
                      valueFormatter={(val) => `${val.toFixed(2)}%`}
                      onLatestCumulativeWinRateChange={setLatestCumulativeWinRate}
                      useHourly={isSingleDaySelection}
                    />
                  </SimpleGrid>
                )}
                </LazyMountWhenVisible>

                {/* Profit & Loss Over Time — when comparison on, show selected then comparison */}
                <LazyMountWhenVisible minHeight={360} data-lazy-mount="overview-pnl-over-time">
                {hasComparison ? (
                  <Box mb="24px">
                    <Box data-statistics-card-surface="" border="2px solid" borderColor="#38BBD1" borderRadius="lg" p={1} mb="24px" bg={cardBg}>
<PnLOverTimeChart
                        filters={cohortFilters}
                        breakdown={isSingleDaySelection ? "hourly" : "daily"}
                        statistics={backendStats}
                        statisticsLoading={statsLoading}
                      />
                    </Box>
                    <Box data-statistics-card-surface="" border="2px solid" borderColor="#F49113" borderRadius="lg" p={1} bg={cardBg}>
<PnLOverTimeChart
                        filters={comparisonChartStatsFilters}
                        breakdown={isSingleDaySelection ? "hourly" : "daily"}
                        statistics={comparisonBackendStats}
                        statisticsLoading={comparisonStatsLoading}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Box mb="24px">
                    <PnLOverTimeChart
                      filters={cohortFilters}
                      breakdown={isSingleDaySelection ? "hourly" : "daily"}
                      statistics={backendStats}
                      statisticsLoading={statsLoading}
                    />
                  </Box>
                )}
                </LazyMountWhenVisible>

                {/* Trade Distribution — when comparison on, show selected then comparison */}
                <LazyMountWhenVisible minHeight={400} data-lazy-mount="overview-distribution">
                {hasComparison ? (
                  <Box mb="24px">
                    <Box data-statistics-card-surface="" border="2px solid" borderColor="#38BBD1" borderRadius="lg" p={1} mb="24px" bg={cardBg}>
<TradeDistributionCharts
                        filters={distributionClosedChartFilters}
                      />
                    </Box>
                    <Box data-statistics-card-surface="" border="2px solid" borderColor="#F49113" borderRadius="lg" p={1} bg={cardBg}>
<TradeDistributionCharts filters={distributionClosedComparisonChartFilters} />
                    </Box>
                  </Box>
                ) : (
                  <Box mb="24px">
                    <TradeDistributionCharts
                      filters={distributionClosedChartFilters}
                    />
                  </Box>
                )}
                </LazyMountWhenVisible>

                {/* Risk Metrics — when comparison on, duplicate with blue / yellow stroke */}
                {hasComparison ? (
                  <LazyMountWhenVisible minHeight={280} data-lazy-mount="overview-risk-metrics">
                  <Box mb="24px">
                    <Box data-statistics-card-surface="" border="2px solid" borderColor="#38BBD1" borderRadius="lg" p={1} mb="24px" bg={cardBg}>
<RiskMetricsCard filters={cohortFilters} />
                    </Box>
                    <Box data-statistics-card-surface="" border="2px solid" borderColor="#F49113" borderRadius="lg" p={1} bg={cardBg}>
<RiskMetricsCard filters={comparisonChartStatsFilters} />
                    </Box>
                  </Box>
                  </LazyMountWhenVisible>
                ) : null}


                {/* Daily Activity — when comparison on, duplicate */}
                <LazyMountWhenVisible minHeight={360} data-lazy-mount="overview-daily-activity">
                {hasComparison ? (
                  <Box mb="24px">
                    <Box data-statistics-card-surface="" border="2px solid" borderColor="#38BBD1" borderRadius="lg" p={1} mb="24px" bg={cardBg}>
<DailyActivityChart filters={dailyActivityFilters} />
                    </Box>
                    <Box data-statistics-card-surface="" border="2px solid" borderColor="#F49113" borderRadius="lg" p={1} bg={cardBg}>
<DailyActivityChart filters={comparisonDailyActivityFilters} />
                    </Box>
                  </Box>
                ) : (
                  <Box mb="24px">
                    <DailyActivityChart filters={dailyActivityFilters} />
                  </Box>
                )}
                </LazyMountWhenVisible>

                {/* Weekly Breakdown — when comparison on, duplicate */}
                <LazyMountWhenVisible minHeight={360} data-lazy-mount="overview-weekly-breakdown">
                {hasComparison ? (
                  <Box mb="24px">
                    <Box data-statistics-card-surface="" border="2px solid" borderColor="#38BBD1" borderRadius="lg" p={1} mb="24px" bg={cardBg}>
<PnLOverTimeChart
                        filters={weeklyBreakdownFilters}
                        breakdown="weekly"
                        title="Weekly Breakdown"
                      />
                    </Box>
                    <Box data-statistics-card-surface="" border="2px solid" borderColor="#F49113" borderRadius="lg" p={1} bg={cardBg}>
<PnLOverTimeChart
                        filters={comparisonWeeklyBreakdownFilters}
                        breakdown="weekly"
                        title="Weekly Breakdown"
                      />
                    </Box>
                  </Box>
                ) : (
                  <Box mb="24px">
                    <PnLOverTimeChart
                      filters={weeklyBreakdownFilters}
                      breakdown="weekly"
                      title="Weekly Breakdown"
                    />
                  </Box>
                )}
                </LazyMountWhenVisible>

                {/* Monthly Performance Table — only show comparison card when selected and comparison are in different month(s)/year(s) */}
                <LazyMountWhenVisible minHeight={320} data-lazy-mount="overview-monthly-performance">
                {showMonthlyPerformanceComparison ? (
                  <Box mb="24px">
                    <Box data-statistics-card-surface="" border="2px solid" borderColor="#38BBD1" borderRadius="lg" p={1} mb="24px" bg={cardBg}>
<MonthlyPerformanceTable filters={monthlyPerformanceFilters} />
                    </Box>
                    <Box data-statistics-card-surface="" border="2px solid" borderColor="#F49113" borderRadius="lg" p={1} bg={cardBg}>
<MonthlyPerformanceTable filters={comparisonMonthlyPerformanceFilters} />
                    </Box>
                  </Box>
                ) : (
                  <Box mb="24px">
                    <MonthlyPerformanceTable filters={monthlyPerformanceFilters} />
                  </Box>
                )}
                </LazyMountWhenVisible>


                {/* Comparative Analysis (Win vs Loss, Long vs Short) — comparison mode */}
                {hasComparison ? (
                  <LazyMountWhenVisible minHeight={280} data-lazy-mount="overview-comparative">
                  <Box mb="24px">
                    <Box data-statistics-card-surface="" border="2px solid" borderColor="#38BBD1" borderRadius="lg" p={1} mb="24px" bg={cardBg}>
<ComparativeAnalysis filters={cohortFilters} statistics={statistics} />
                    </Box>
                    <Box data-statistics-card-surface="" border="2px solid" borderColor="#F49113" borderRadius="lg" p={1} bg={cardBg}>
<ComparativeAnalysis filters={comparisonChartStatsFilters} statistics={comparisonStatistics} />
                    </Box>
                  </Box>
                  </LazyMountWhenVisible>
                ) : null}


              </>
              </>
              )}
          </TabsContent>

          {visitedStatisticsTabs.has("detailed") ? (
          <TabsContent
            value="detailed"
            forceMount
            className={statisticsTabPanelClassName}
          >
                {!tradeStatisticsHasData(backendStats) ? (
                  <Flex flex="1" minH="0" w="100%" align="center" justify="center">
                    <StatisticsEmptyState variant="no-data" fillParent />
                  </Flex>
                ) : (
                  <>
                    <SimpleGrid columns={{ base: 1, lg: 3 }} spacing="24px" mb="24px">
                      <ComparativeAnalysis
                        filters={cohortFilters}
                        statistics={statistics}
                      />
                      <StreakAnalysis statistics={backendStats} filters={cohortFilters} />
                      <PerformanceBreakdown
                        symbolStats={symbolStats}
                        statistics={{
                          largestWin: statistics.largestWin,
                          largestLoss: statistics.largestLoss,
                          closedTrades: statistics.closedTrades,
                          winningTrades: statistics.winningTrades,
                          losingTrades: statistics.losingTrades,
                          totalReturnPercent: statistics.totalReturnPercent,
                          averageReturnPercent: statistics.averageReturnPercent,
                        }}
                        backendStats={
                          backendStats
                            ? {
                                best_trade: backendStats.best_trade,
                                worst_trade: backendStats.worst_trade,
                                most_traded_symbol: backendStats.most_traded_symbol,
                                most_traded_symbol_count: backendStats.most_traded_symbol_count,
                              }
                            : null
                        }
                      />
                    </SimpleGrid>
                    <SimpleGrid columns={{ base: 1, lg: 3 }} spacing="24px">
                      <SystemQualityTable
                        statistics={{
                          totalPnL: statistics.totalPnL,
                          totalTrades: statistics.totalTrades,
                        }}
                        backendStats={backendStats}
                      />
                      <AveragesReturnsTable
                        statistics={{
                          averageWin: statistics.averageWin,
                          averageLoss: statistics.averageLoss,
                        }}
                        backendStats={backendStats}
                      />
                      <TimingCostsTable backendStats={backendStats} />
                    </SimpleGrid>
                  </>
                )}
          </TabsContent>
          ) : null}

          {visitedStatisticsTabs.has("risk-metrics") ? (
          <TabsContent
            value="risk-metrics"
            forceMount
            className={statisticsTabPanelClassName}
          >
              <RiskMetricsTab
                filters={cohortFilters}
                comparisonFilters={hasComparison ? comparisonChartStatsFilters : undefined}
              />
          </TabsContent>
          ) : null}

          {visitedStatisticsTabs.has("time-analysis") ? (
          <TabsContent
            value="time-analysis"
            forceMount
            className={statisticsTabPanelClassName}
          >
              <TimeAnalysisTab
                filters={cohortFilters}
                comparisonFilters={hasComparison ? comparisonChartStatsFilters : undefined}
                showMonthlyComparison={showMonthlyPerformanceComparison}
              />
          </TabsContent>
          ) : null}

          {visitedStatisticsTabs.has("symbol-performance") ? (
          <TabsContent
            value="symbol-performance"
            forceMount
            className={statisticsTabPanelClassName}
          >
              <SymbolPerformanceTab
                filters={listStatsFilters}
                comparisonFilters={hasComparison ? comparisonListStatsFilters : undefined}
              />
          </TabsContent>
          ) : null}

          {visitedStatisticsTabs.has("session-performance") ? (
          <TabsContent
            value="session-performance"
            forceMount
            className={statisticsTabPanelClassName}
          >
              <SessionPerformanceTab
                filters={cohortFilters}
                comparisonFilters={hasComparison ? comparisonChartStatsFilters : undefined}
              />
          </TabsContent>
          ) : null}
        </Tabs>
      )}
    </Flex>
    </StatisticsPageErrorProvider>
  );
}

export default Statistics;

